import * as yup from "yup";
import { Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { Button } from "../../../components/ui/button";
import Common from "../../../helper/common";
import {
    FormikInput,
    PhoneInputField,
    FormikTextArea,
} from "../../../components/formik-components";
import { AppCheckBox } from "../../../components/appComponents";
import { ResultStatus, respEnum } from "../../../constants/appConstant";
import toast from "react-hot-toast";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../../components/ui/dialog";
import { useNavigate } from "react-router-dom";

// A more comprehensive country phone rules database
const countryPhoneRules = {
  // Format: country code -> rules object
  1: {
    // USA/Canada
    format: "xxx-xxx-xxxx",
    validLengths: [10], // Valid number lengths after removing formatting
    hasLeadingZero: false, // Whether the number can start with 0
    mobilePrefix: null, // Special prefix for mobile numbers if any
    description: "US/Canada",
  },
  44: {
    // UK
    format: "xxxx xxx xxxx",
    validLengths: [10, 11],
    hasLeadingZero: true, // UK mobile numbers start with '07'
    mobilePrefix: "7",
    description: "United Kingdom",
  },
  91: {
    // India
    format: "xxxxx xxxxx",
    validLengths: [10],
    hasLeadingZero: false,
    mobilePrefix: null,
    description: "India",
  },
  92: {
    // Pakistan
    format: "xxx xxxxxxx",
    validLengths: [10],
    hasLeadingZero: true, // Pakistan mobile numbers can start with '0'
    mobilePrefix: "3", // Most Pakistan mobile numbers start with 3
    description: "Pakistan",
  },
  86: {
    // China
    format: "xxx xxxx xxxx",
    validLengths: [11],
    hasLeadingZero: false,
    mobilePrefix: "1",
    description: "China",
  },
  33: {
    // France
    format: "x xx xx xx xx",
    validLengths: [9],
    hasLeadingZero: true,
    mobilePrefix: "6",
    description: "France",
  },
  49: {
    // Germany
    format: "xxx xxxxxxxx",
    validLengths: [10, 11],
    hasLeadingZero: true,
    mobilePrefix: "1",
    description: "Germany",
  },
  81: {
    // Japan
    format: "xx xxxx xxxx",
    validLengths: [10],
    hasLeadingZero: false,
    mobilePrefix: "80",
    description: "Japan",
  },
  82: {
    // South Korea
    format: "xx xxxx xxxx",
    validLengths: [10],
    hasLeadingZero: false,
    mobilePrefix: "1",
    description: "South Korea",
  },
  7: {
    // Russia
    format: "xxx xxx xx xx",
    validLengths: [10],
    hasLeadingZero: false,
    mobilePrefix: "9",
    description: "Russia",
  },
  55: {
    // Brazil
    format: "xx xxxxx xxxx",
    validLengths: [10, 11],
    hasLeadingZero: false,
    mobilePrefix: null,
    description: "Brazil",
  },
  971: {
    // UAE
    format: "xx xxx xxxx",
    validLengths: [9],
    hasLeadingZero: false,
    mobilePrefix: "5",
    description: "UAE",
  },
  966: {
    // Saudi Arabia
    format: "xx xxx xxxx",
    validLengths: [9],
    hasLeadingZero: false,
    mobilePrefix: "5",
    description: "Saudi Arabia",
  },
  234: {
    // Nigeria
    format: "xx xxxx xxxx",
    validLengths: [10],
    hasLeadingZero: true,
    mobilePrefix: "7",
    description: "Nigeria",
  },
  27: {
    // South Africa
    format: "xx xxx xxxx",
    validLengths: [9],
    hasLeadingZero: true,
    mobilePrefix: "6",
    description: "South Africa",
  },
  // Add more countries as needed
};

// Default rules for countries not specifically listed
const defaultPhoneRules = {
  format: "xxxxxxxxxx",
  validLengths: [7, 8, 9, 10, 11, 12, 13, 14, 15],
  hasLeadingZero: true,
  mobilePrefix: null,
  description: "International",
};

// Function to detect country code from a phone number
const detectCountryCode = (phone) => {
  if (!phone || typeof phone !== "string") return null;

  // Remove any non-digit characters except the leading '+' sign
  const cleanPhone = phone.replace(/[^\d+]/g, "");

  // Must start with a '+' sign
  if (!cleanPhone.startsWith("+")) return null;

  // Remove the '+' sign and check the remainder
  const numberWithoutPlus = cleanPhone.substring(1);
  if (!numberWithoutPlus) return null;

  // Try to match country codes from longest to shortest
  // This handles cases where one country code is a prefix of another
  // (e.g., '1' for US/Canada vs '1246' for Barbados)
  const sortedCodes = Object.keys(countryPhoneRules).sort(
    (a, b) => b.length - a.length
  );

  for (const code of sortedCodes) {
    if (numberWithoutPlus.startsWith(code)) {
      return {
        code,
        rules: countryPhoneRules[code],
        number: numberWithoutPlus.substring(code.length),
      };
    }
  }

  // If we couldn't find a specific country code, use default rules
  // Just use the first 1-3 digits as a "generic" country code
  const genericCode = numberWithoutPlus.substring(
    0,
    Math.min(3, numberWithoutPlus.length)
  );
  return {
    code: genericCode,
    rules: defaultPhoneRules,
    number: numberWithoutPlus.substring(genericCode.length),
  };
};

// Function to normalize phone number format
const normalizePhoneNumber = (phone) => {
  if (!phone || typeof phone !== "string") return "";

  // Clean the phone number to just digits and plus sign
  return phone.replace(/[^\d+]/g, "");
};

// Custom validation for phone numbers based on country code
const validatePhoneNumber = (phone) => {
  if (!phone || typeof phone !== "string") return false;

  // Normalize the phone number
  const normalizedPhone = normalizePhoneNumber(phone);
  if (!normalizedPhone.startsWith("+")) return false;

  // Detect country code and get rules
  const countryInfo = detectCountryCode(normalizedPhone);
  if (!countryInfo) return false;

  const { code, rules, number } = countryInfo;

  // Clean the number part (remove leading zero if needed)
  let cleanNumber = number;
  if (rules.hasLeadingZero && cleanNumber.startsWith("0")) {
    cleanNumber = cleanNumber.substring(1);
  }

  // Check if the number length matches any of the valid lengths
  return rules.validLengths.includes(cleanNumber.length);
};

// Get error message for invalid phone numbers
const getErrorMessage = (phone) => {
  if (!phone) return "Phone number is required";
  if (typeof phone !== "string") return "Invalid phone number format";
  // Normalize the phone number
  const normalizedPhone = normalizePhoneNumber(phone);
  if (!normalizedPhone.startsWith("+"))
    return "Phone number must start with a country code (e.g., +1, +92)";

  // Detect country code and get rules
  const countryInfo = detectCountryCode(normalizedPhone);
  if (!countryInfo) return "Could not determine country code";

  const { code, rules, number } = countryInfo;

  // Clean the number part (remove leading zero if needed)
  let cleanNumber = number;
  if (rules.hasLeadingZero && cleanNumber.startsWith("0")) {
    cleanNumber = cleanNumber.substring(1);
  }

  // Check if the length is valid
  if (!rules.validLengths.includes(cleanNumber.length)) {
    const lengthsText =
      rules.validLengths.length > 1
        ? `one of ${rules.validLengths.join(", ")}`
        : rules.validLengths[0];

    const leadingZeroText = rules.hasLeadingZero
      ? " (any leading zero will be removed)"
      : "";

    return `Invalid phone number length for ${rules.description}. Should be ${lengthsText} digits after country code${leadingZeroText}.`;
  }

  return "";
};

// Helper function to parse a phone number into country code and number
const parsePhoneNumber = (phone) => {
  if (!phone || typeof phone !== "string")
    return { phoneCode: "+92", phoneNum: "" };

  const countryInfo = detectCountryCode(phone);
  if (!countryInfo) return { phoneCode: "+92", phoneNum: "" };

  return {
    phoneCode: `+${countryInfo.code}`,
    phoneNum: countryInfo.number,
  };
};

// Function to format phone number with country code when saving
const formatPhoneNumber = (phoneCode, phoneNum) => {
  if (!phoneCode && !phoneNum) return "";

  // Normalize phone code to ensure it has a plus sign
  const normalizedCode = phoneCode.startsWith("+")
    ? phoneCode
    : `+${phoneCode}`;

  // Remove any non-digit characters from the phone number
  let cleanNumber = phoneNum.replace(/[^\d]/g, "");

  // Get the country code without the plus sign
  const codeWithoutPlus = normalizedCode.substring(1);

  // Check if the country has specific rules
  const rules = countryPhoneRules[codeWithoutPlus] || defaultPhoneRules;

  // Handle leading zeros based on country rules
  if (rules.hasLeadingZero && cleanNumber.startsWith("0")) {
    // Some countries (like Pakistan) might have optional leading zero
    // We'll remove it as the country code already identifies the country
    cleanNumber = cleanNumber.substring(1);
  }

  return `${normalizedCode}${cleanNumber}`;
};

// Function to display validation issues on Save button click and in console for debugging
const debugPhoneValidation = (phone) => {
  if (!phone || typeof phone !== "string") {
    console.log("Invalid phone - null, undefined or not a string:", phone);
    return;
  }

  console.group("Phone Validation Debug");
  console.log("Phone number:", phone);

  // Normalize the phone number
  const normalizedPhone = normalizePhoneNumber(phone);
  console.log("Normalized phone:", normalizedPhone);

  // Detect country code
  const countryInfo = detectCountryCode(normalizedPhone);
  if (!countryInfo) {
    console.log("Failed to detect country code");
    console.groupEnd();
    return;
  }

  const { code, rules, number } = countryInfo;
  console.log("Detected country code:", code);
  console.log("Country rules:", rules);
  console.log("Number portion:", number);

  // Clean the number part (remove leading zero if needed)
  let cleanNumber = number;
  if (rules.hasLeadingZero && cleanNumber.startsWith("0")) {
    cleanNumber = cleanNumber.substring(1);
    console.log("Removed leading zero, clean number:", cleanNumber);
  }

  // Check if the length is valid
  const isValidLength = rules.validLengths.includes(cleanNumber.length);
  console.log("Valid lengths:", rules.validLengths);
  console.log("Current length:", cleanNumber.length);
  console.log("Is valid length:", isValidLength);

  // Overall validation result
  const isValid = validatePhoneNumber(phone);
  console.log("Overall validation result:", isValid);

  if (!isValid) {
    console.log("Error message:", getErrorMessage(phone));
  }

  console.groupEnd();
};

const FormikInitialValues = {
    phone: "",
    phoneCode: "+92",
    phoneNum: "",
    reason: "",
    appBooking: false,
    clubBooking: false,

    // From global 
    myAppBooking: false,
    webBooking: false,
    whatsappBooking: false,
};

const createFormikValidation = (availableBookingTypes, isEditMode = false) => {
    const validationObject = {
       phone: isEditMode 
         ? yup.string() // No validation in edit mode
         : yup
             .string()
             .required("Phone Number is required"),
        
      reason: yup
        .string()
        .required("Reason for blocking is required")
        .min(8, "Reason must be at least 8 characters")
        .max(1000, "Reason cannot exceed 1000 characters")
        .test('not-empty', 'Reason for blocking is required', function (value) {
          return value && value.trim().length >= 8;
        }),
    };
  
    // Dynamically add validation for booking types (as boolean)
    availableBookingTypes.forEach((bookingType) => {
      validationObject[bookingType.key] = yup.boolean();
    });
  
    // Add custom test for at least one booking type selected
    return yup.object(validationObject).test(
      'at-least-one-platform',
      'Please select at least one platform to block booking from',
      function (values) {
        return availableBookingTypes.some((bookingType) => values[bookingType.key]);
      }
    );
  };
  



const AddBlackListCustomer = ({ id, onClose, open, setOpen, bookingModal, availableBookingTypes = [] }) => {
    const formikRef = useRef();
    const dialogRef = useRef();
    const navigate = useNavigate();
    const [isFormikInitialized, setIsFormikInitialized] = useState(false);

    const [isPhonePresent, setIsPhonePresent] = useState(false);
    const [mode, setMode] = useState(id ? true : false);
    const [platformError, setPlatformError] = useState("");

    useEffect(() => {
        setMode(id ? true : false);
        setIsFormikInitialized(open);
    }, [open, id]);

    const onSubmitAddBlackListUser = async (d, action) => {
        // Construct the full phone number from phoneCode and phoneNum
        let phoneToValidate = "";
        
        if (d.phone && d.phone.startsWith("+")) {
            // If phone already has country code, use it directly
            phoneToValidate = d.phone;
        } else if (d.phoneCode && d.phoneNum) {
            // Use the formatPhoneNumber helper to construct the full phone number
            phoneToValidate = formatPhoneNumber(d.phoneCode, d.phoneNum);
        } else if (d.phone) {
            // Fallback to just phone if available
            phoneToValidate = d.phone;
        }

        if (!phoneToValidate) {
            toast.error("Phone is Required");
            action.setSubmitting(false);
            return;
        }

        // Skip phone validation in edit mode since phone is disabled
        if (!mode) {
            // Debug phone validation
            console.log("Validating phone number:", phoneToValidate);
            debugPhoneValidation(phoneToValidate);

            // Check phone validation
            const isPhoneValid = validatePhoneNumber(phoneToValidate);
            if (!isPhoneValid) {
                const errorMessage = getErrorMessage(phoneToValidate);
                toast.error(errorMessage);
                action.setSubmitting(false);
                return;
            }
        }

        // Extract phone code and local number from the validated phone
        let phoneCode = "";
        let localPhoneNumber = "";
        
        if (phoneToValidate.startsWith("+")) {
            const countryInfo = detectCountryCode(phoneToValidate);
            if (countryInfo) {
                phoneCode = `+${countryInfo.code}`;
                localPhoneNumber = countryInfo.number;
            } else {
                // Fallback: try to extract manually
                const match = phoneToValidate.match(/^\+(\d{1,3})(.+)$/);
                if (match) {
                    phoneCode = `+${match[1]}`;
                    localPhoneNumber = match[2];
                } else {
                    phoneCode = d.phoneCode || "+92";
                    localPhoneNumber = phoneToValidate.replace(/^\+/, "");
                }
            }
        } else {
            phoneCode = d.phoneCode || "+92";
            localPhoneNumber = phoneToValidate;
        }

        let data = {
            ...d,
            id,
            phone: localPhoneNumber, // Send only local number without country code
            phoneCode: phoneCode,    // Send country code separately
            reason: d.reason,
        };

        // Debug: Log what's being sent to API
        console.log("API Data being sent:", {
            phone: localPhoneNumber,
            phoneCode: phoneCode,
            originalPhone: phoneToValidate,
            reason: d.reason
        });

        let response = await Common.ApiService.getInstance().request(
            "AddUpdateBlackListUser",
            data,
            "POST"
        );
        action.setSubmitting(false);
        if (response.status == ResultStatus.Success) {
            Common.showToast("Saved successfully", "Success", "success");
            onClose(response.data, d.phone);
            setOpen(false);
            if (!onClose) {
                navigate("/app/blacklistcustomers", { replace: true });
            }
        }
    };


    const getCustomerInfo = async () => {
        if (mode) {
          return;
        }
    
        let response = await Common.ApiService.getInstance().request(
          `GetBlackListUserDetails?id=${id}`
        );
    
        if (response.status === ResultStatus.Success) {
          debugger;
    
          if (id && response?.data?.length) {
            const customer = response.data[0];
            console.log("Customer data from API:", customer);
            
            // Parse the phone number to get proper country code and number
            const parsedPhone = parsePhoneNumber(customer.phone || customer.phoneNum || "");
            
            const transformedData = {
              ...customer,
              phone: customer.phone || customer.phoneNum || "",
              phoneNum: parsedPhone.phoneNum,
              phoneCode: parsedPhone.phoneCode,
              reason: customer.reason || customer.reasonText || "", // Map reason field properly
              appBooking: customer.app_booking, // API uses snake_case
              clubBooking: customer.club_booking,
              myAppBooking: customer.my_app_booking,
              webBooking: customer.web_booking,
              whatsappBooking: customer.whatsapp_booking,
            };
            
            console.log("Transformed data:", transformedData);

            // Set all values at once
            formikRef.current.setValues({
              ...formikRef.current.values, // Preserve existing values
              ...transformedData, // Update with transformed data
            });
            
            // Also set each field individually to ensure they update
            formikRef.current.setFieldValue("reason", transformedData.reason);
            formikRef.current.setFieldValue("appBooking", transformedData.appBooking);
            formikRef.current.setFieldValue("clubBooking", transformedData.clubBooking);
            formikRef.current.setFieldValue("myAppBooking", transformedData.myAppBooking);
            formikRef.current.setFieldValue("webBooking", transformedData.webBooking);
            formikRef.current.setFieldValue("whatsappBooking", transformedData.whatsappBooking);
            
            console.log("Form values after setting:", formikRef.current.values);
            console.log("Available booking types:", availableBookingTypes);
            
            const isPhoneNumberPresent = customer.phoneNum?.trim()?.length > 0;

            setIsPhonePresent(isPhoneNumberPresent);
          } else {
            setIsPhonePresent(!mode);
          }
        }
      };
      useEffect(() => {
        if (mode) {
          getCustomerInfo();
        } else {
          // On add mode, reset phone presence and form
          setIsPhonePresent(!mode);
          // formikRef.current?.resetForm();
        }
      }, [mode]);
    
      useEffect(() => {
        if (id) {
          getCustomerInfo();
        }
      }, [id]);

    return (
        <div>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                    {bookingModal ? (
                        <Button
                            size="sm"
                            variant="ghost"
                            className="group px-2 py-1 text-xs cus-sty-01"
                        >
                            <Icon icon="heroicons:plus-20-solid" className="h-5 w-5" />
                        </Button>
                    ) : (
                        <Button
                            size="sm"
                            variant="primary"
                            className="flex items-center gap-2 whitespace-nowrap"
                        >
                            <Icon icon="heroicons:plus-20-solid" className="h-4 w-4" />
                            <span className="hidden sm:inline">Add</span>
                        </Button>
                    )}
                </DialogTrigger>

                <DialogContent className="custom-dialog">
                    <DialogHeader>
                        <DialogTitle>{id ? "Update" : "Add"} Black List Customer</DialogTitle>
                    </DialogHeader>

                    <div className="text-sm text-default-500  space-y-4">
                        <Formik
                            innerRef={formikRef}
                            onSubmit={onSubmitAddBlackListUser}
                            initialValues={FormikInitialValues}
                            validationSchema={createFormikValidation(availableBookingTypes, mode)}
                            validateOnChange={false}
                            validateOnBlur={false}
                        >
                            {(formikProps) => (
                                <div className="md:grid md:grid-cols-1 gap-2 space-y-6 md:space-y-0">
                                    <div className="col-span-1">
                                        <div className="md:grid md:grid-cols-1  gap-2  space-y-6 md:space-y-0">
                                            {!mode ? (
                                                <div className="flex flex-col gap-2">
                                                    <PhoneInputField 
                                                        formikProps={formikProps}
                                                        onChange={(values) => {
                                                            // Phone validation will be handled on submit
                                                        }}
                                                    />
                                                </div>
                                            ) : (
                                                <div className="flex flex-col gap-2">
                                                    <label className="block text-sm font-medium text-gray-700">
                                                        Phone Number
                                                    </label>
                                                    <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                                                        {formikProps.values.phoneCode} {formikProps.values.phoneNum || formikProps.values.phone}
                                                    </div>
                                                </div>
                                            )}
                                            <div className="flex flex-col gap-2">
                                                <FormikTextArea
                                                    formikProps={formikProps}
                                                    name={"reason"}
                                                    label={"Reason"}
                                                    placeholder={
                                                        "Enter customer reason (min 8 characters)"
                                                    }
                                                    maxLength={1000}
                                                    rows={3}
                                                    required={true}
                                                />
                                            </div>
                                            <div className="flex flex-col gap-2 mt-4">
                                                <label className={`block text-sm font-medium ${formikProps.errors.atLeastOnePlatform ? 'text-red-700' : 'text-gray-700'}`}>
                                                    Block Booking From <span className="text-red-500">*</span>
                                                </label>
                                                <div className="flex flex-wrap gap-4 justify-center items-center">
                                                    {availableBookingTypes.map((bookingType) => (
                                                    <AppCheckBox
                                                            key={bookingType.key}
                                                            text={bookingType.label}
                                                            checked={Boolean(formikProps.values[bookingType.key])}
                                                        onChange={(checked) => {
                                                                formikProps.setFieldValue(bookingType.key, checked);
                                                            // Clear platform error when any checkbox is selected
                                                            setPlatformError("");
                                                        }}
                                                        className="border-default-300"
                                                    />
                                                    ))}
                                                </div>
                                                {/* Validation error for platform selection */}
                                                {platformError && (
                                                    <div className="text-red-500 text-sm mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                                                        <div className="flex items-center gap-2">
                                                            <Icon icon="heroicons:exclamation-triangle" className="h-4 w-4 text-red-500" />
                                                            <span className="font-medium">{platformError}</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Formik>
                    </div>
                    <DialogFooter className="-2">
                        <DialogClose asChild>
                            <Button type="button" variant="outline">
                                Close
                            </Button>
                        </DialogClose>

                        {isFormikInitialized && formikRef.current && (
                            <Button
                                type="button"
                                onClick={async (e) => {
                                    e.preventDefault();
                                    
                                    // Trigger validation first
                                    const errors = await formikRef.current.validateForm();
                                    
                                    // Manually check platform selection
                                    const values = formikRef.current.values;
                                    const hasAtLeastOnePlatform = values.appBooking || values.clubBooking || values.myAppBooking || values.webBooking || values.whatsappBooking;
                                    
                                    if (!hasAtLeastOnePlatform) {
                                        // Set platform error state
                                        setPlatformError("Please select at least one platform to block booking from");
                                        return;
                                    }
                                    
                                    // Check if there are any other errors
                                    if (Object.keys(errors).length > 0) {
                                        return;
                                    }
                                    
                                    // If no errors, proceed with submission
                                    formikRef.current?.handleSubmit();
                                }}
                            >
                                {formikRef.current.isSubmitting
                                    ? "Saving..."
                                    : !id
                                        ? "Add"
                                        : "Update"}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AddBlackListCustomer;
